/** Sales order detail with the tabs from §4.5. Billing arrives in Phase 5. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { FolderKanban, Target } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { QuotationsCard } from '../purchasing/QuotationsCard';
import { PurchaseOrdersCard } from '../purchasing/PurchaseOrdersCard';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
} from '../../components/ui';
import { PAYMENT_LABELS, PROJECT_TONES } from './SalesOrdersPage';

interface SalesOrderDetail {
  id: string;
  code: string;
  projectName: string;
  confirmedAt: string;
  clientPoRef: string | null;
  paymentTermsDays: number;
  amountVatEx: string;
  vat: string;
  totalContractPrice: string;
  ewtAmount: string;
  wvatAmount: string;
  projectCost?: string;
  grossProfit?: string;
  netProfit?: string;
  totalCommission?: string;
  isGovernment: boolean;
  mrcAmountVatEx: string | null;
  recurringStart: string | null;
  recurringMonths: number | null;
  billingDay: number | null;
  autoIssue: boolean;
  billedTotal: string;
  collectedTotal: string;
  outstanding: string;
  contractBalance: string;
  awaitingConfirmation: string;
  unbilled: string;
  paymentStatus: keyof typeof PAYMENT_LABELS;
  customer: { id: string; name: string; code: string; tin: string | null };
  paymentTerms: { label: string } | null;
  items: { id: string; lineNo: number; name: string; unit: string | null; qty: string; amountVatEx: string }[];
  lead: { id: string; code: string } | null;
  source?: 'LEAD' | 'MANUAL';
  legacyRef?: string | null;
  project: { id: string; code: string; status: string; _count: { deliverables: number } } | null;
  billings: {
    id: string;
    code: string;
    type: string;
    billingDate: string;
    dueDate: string;
    total: string;
    settledAmount: string;
    status: string;
  }[];
}

type Tab = 'summary' | 'items' | 'billings' | 'purchasing' | 'recurring' | 'project';

export function SalesOrderDetailPage(): JSX.Element {
  const { id } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('summary');

  const order = useQuery({
    queryKey: ['sales-order', id],
    queryFn: () => api<{ data: SalesOrderDetail }>(`/sales-orders/${id}`),
  });

  if (order.isLoading) return <Skeleton className="h-96 w-full" />;
  const row = order.data?.data;
  if (!row) return <Alert tone="critical">Sales order not found.</Alert>;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'items', label: `Items (${row.items.length})` },
    { key: 'billings', label: `Billings (${row.billings.length})` },
    { key: 'purchasing', label: 'Purchasing' },
    { key: 'recurring', label: 'Recurring' },
    { key: 'project', label: 'Project' },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-navy dark:text-white">{row.projectName}</h1>
            <StatusChip tone={PAYMENT_LABELS[row.paymentStatus].tone}>
              {PAYMENT_LABELS[row.paymentStatus].label}
            </StatusChip>
            {row.isGovernment && <StatusChip tone="info">Government</StatusChip>}
          </div>
          <p className="text-sm text-slate-500">
            <span className="font-mono">{row.code}</span> ·{' '}
            <Link to={`/customers/${row.customer.id}`} className="hover:underline">
              {row.customer.name}
            </Link>{' '}
            · confirmed {shortDate(row.confirmedAt)}
            {row.clientPoRef ? ` · PO ${row.clientPoRef}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {row.lead ? (
            <Link to={`/leads/${row.lead.id}`}>
              <StatusChip tone="neutral">
                <Target aria-hidden="true" className="h-3 w-3" /> {row.lead.code}
              </StatusChip>
            </Link>
          ) : (
            <StatusChip tone="warning">Manual{row.legacyRef ? ` · ${row.legacyRef}` : ''}</StatusChip>
          )}
          {row.project && (
            <Link to={`/projects/${row.project.id}`}>
              <StatusChip tone={PROJECT_TONES[row.project.status] ?? 'neutral'}>
                <FolderKanban aria-hidden="true" className="h-3 w-3" /> {row.project.code}
              </StatusChip>
            </Link>
          )}
        </div>
      </header>

      <div
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
        role="tablist"
        aria-label="Sales order sections"
      >
        {tabs.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={`shrink-0 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-medium ${
              tab === item.key
                ? 'bg-navy text-white'
                : 'border border-slate-200 bg-white text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'summary' && (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card title="Contract" description="Frozen at confirmation; later rate changes never move it.">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Money label="Amount (VAT-ex)" value={row.amountVatEx} />
              <Money label="VAT" value={row.vat} />
              <Money label="Total contract price" value={row.totalContractPrice} strong />
              <Money label="Expected EWT" value={row.ewtAmount} />
              {row.isGovernment && <Money label="Withholding VAT" value={row.wvatAmount} />}
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Payment terms</dt>
                <dd>
                  {row.paymentTerms?.label ?? `${row.paymentTermsDays} days`}
                </dd>
              </div>
            </dl>
          </Card>

          <Card title="Position">
            <dl className="grid gap-3 sm:grid-cols-2">
              <Money label="Billed to date" value={row.billedTotal} />
              <Money label="Collected to date" value={row.collectedTotal} />
              <Money label="Contract balance" value={row.contractBalance} strong />
              <Money label="Due now (billed, unpaid)" value={row.outstanding} />
              <Money label="Still to bill" value={row.unbilled} />
              {Number(row.awaitingConfirmation) > 0 && (
                <div className="sm:col-span-2">
                  <Alert tone="warning">
                    {peso(row.awaitingConfirmation)} has been recorded but not confirmed yet, so it
                    has not come off the outstanding balance. Confirm it under Billing →
                    Collections.
                  </Alert>
                </div>
              )}
            </dl>
          </Card>

          {can('leads.view_financials') && (
            <Card title="Margin" className="lg:col-span-2">
              <dl className="grid gap-3 sm:grid-cols-4">
                <Money label="Project cost" value={row.projectCost} />
                <Money label="Gross profit" value={row.grossProfit} />
                {row.totalCommission !== undefined && (
                  <Money label="Commission" value={row.totalCommission} />
                )}
                {row.netProfit !== undefined && (
                  <Money label="Net profit" value={row.netProfit} strong />
                )}
              </dl>
            </Card>
          )}
        </div>
      )}

      {tab === 'items' && (
        <Card title="Line items">
          <TableWrap>
            <thead>
              <tr>
                <Th>#</Th>
                <Th>Deliverable</Th>
                <Th>Unit</Th>
                <Th align="right">Qty</Th>
                <Th align="right">Amount (VAT-ex)</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {row.items.map((item) => (
                <tr key={item.id}>
                  <Td>{item.lineNo}</Td>
                  <Td>{item.name}</Td>
                  <Td>{item.unit ?? '—'}</Td>
                  <Td align="right" className="tabular">
                    {item.qty}
                  </Td>
                  <Td align="right" className="tabular">
                    {peso(item.amountVatEx)}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}

      {tab === 'billings' && (
        <Card title="Billings">
          {row.billings.length === 0 ? (
            <EmptyState
              title="Nothing billed yet"
              description="Statements of account raised against this order appear here."
            />
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Statement</Th>
                  <Th>Type</Th>
                  <Th>Billed</Th>
                  <Th>Due</Th>
                  <Th align="right">Amount</Th>
                  <Th align="right">Settled</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {row.billings.map((billing) => (
                  <tr key={billing.id}>
                    <Td className="font-mono text-xs">{billing.code}</Td>
                    <Td>{billing.type.toLowerCase()}</Td>
                    <Td>{shortDate(billing.billingDate)}</Td>
                    <Td>{shortDate(billing.dueDate)}</Td>
                    <Td align="right" className="tabular">
                      {peso(billing.total)}
                    </Td>
                    <Td align="right" className="tabular">
                      {peso(billing.settledAmount)}
                    </Td>
                    <Td>{billing.status}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      )}

      {tab === 'purchasing' && (
        <div className="space-y-5">
          {row.lead ? (
            <>
              <QuotationsCard leadId={row.lead.id} readOnly />
              <PurchaseOrdersCard salesOrderId={row.id} leadId={row.lead.id} />
            </>
          ) : (
            <Alert tone="info">A manual order (an old project billed in SF) has no quotations or purchase orders.</Alert>
          )}
        </div>
      )}

      {tab === 'recurring' && (
        <Card title="Recurring charge" description="Billed monthly, separate from the contract amount.">
          {row.mrcAmountVatEx ? (
            <dl className="grid gap-3 sm:grid-cols-4">
              <Money label="Monthly charge (VAT-ex)" value={row.mrcAmountVatEx} strong />
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Starts</dt>
                <dd>{shortDate(row.recurringStart)}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Months</dt>
                <dd>{row.recurringMonths ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Billing day</dt>
                <dd>
                  {row.billingDay ?? '—'} {row.autoIssue ? '· issued automatically' : '· left as draft'}
                </dd>
              </div>
            </dl>
          ) : (
            <EmptyState
              title="No recurring charge"
              description="This contract is billed once, against its deliverables."
            />
          )}
        </Card>
      )}

      {tab === 'project' && (
        <Card title="Delivery">
          {row.project ? (
            <div className="flex flex-wrap items-center gap-4">
              <StatusChip tone={PROJECT_TONES[row.project.status] ?? 'neutral'}>
                {row.project.status.charAt(0) + row.project.status.slice(1).toLowerCase()}
              </StatusChip>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {row.project._count.deliverables} deliverable
                {row.project._count.deliverables === 1 ? '' : 's'} on the board
              </span>
              <Link
                to={`/projects/${row.project.id}`}
                className="text-sm font-semibold text-brand hover:underline"
              >
                Open {row.project.code} →
              </Link>
            </div>
          ) : (
            <EmptyState title="No project linked to this order." />
          )}
        </Card>
      )}
    </div>
  );
}

function Money({
  label,
  value,
  strong,
}: {
  label: string;
  value?: string | null;
  strong?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`tabular ${strong ? 'text-lg font-bold text-navy dark:text-white' : ''}`}>
        {peso(value)}
      </dd>
    </div>
  );
}
